import { OidcClient, WebStorageStateStore } from 'oidc-client-ts';
import { IDP_AUTHORITY, IDP_CLIENT_ID, IDP_TENANT_ID } from '@/runtime-config';
import { AUTH_CALLBACK_ROUTE } from './constants';

/**
 * The one module that talks to Invent's IdP: it starts a sign-in there, redeems the code the
 * browser brings back to `/auth/callback`, and names the IdP's end session for logout.
 *
 * Authorization code with PKCE (S256), as the portal's public client. The browser exchanges the
 * code itself — the IdP's token endpoint answers CORS for the portal's registered origins — and
 * the access token it gets is handed to survey-api once, for its own session, and dropped. The
 * id_token is returned for the caller to keep as logout's `id_token_hint`, nothing else. No
 * refresh token is asked for: survey-api's session lives by its own limits, which no IdP token
 * extends.
 *
 * `oidc-client-ts`'s low-level `OidcClient` alone, its request state — the PKCE verifier and the
 * `state` value — in `sessionStorage`: a sign-in belongs to the tab that started it and dies with
 * it. Rejected: its `UserManager`, and `react-oidc-context` over it — silent renewal and a stored
 * IdP user, which nothing here reads; hand-written PKCE — security code this repo would own.
 *
 * **The code comes back in the URL's fragment** (`response_mode: 'fragment'`), never its query:
 * nginx logs the request line, and a single-use secret travels where no server sees it.
 *
 * **Every sign-in names Entra External** (`idp:invent` in `acr_values`): its email and password,
 * whatever the address's domain. Rejected: the IdP's choice by domain — Invent's corporate
 * Microsoft for `invent.us`, another company's own sign-in for a domain it registered, a social
 * one for a public mailbox once IAM turns it on — so an advisor cannot know which page comes,
 * and an alias such as `name+x@invent.us`, which redeems an invitation, cannot sign in at the
 * corporate one. Staff of Invent sign in here with the password they set at the redeem, without
 * corporate SSO or its MFA.
 */

const SCOPE = 'openid profile email api.default';

let client: Nullable<OidcClient> = null;

/** Made when first needed — a sign-in, or a logout's end session — rather than at import, so a
 *  page that does neither never reads the IdP's discovery document. */
function oidc(): OidcClient {
  client ??= new OidcClient({
    authority: IDP_AUTHORITY,
    client_id: IDP_CLIENT_ID,
    redirect_uri: `${window.location.origin}${AUTH_CALLBACK_ROUTE}`,
    response_type: 'code',
    response_mode: 'fragment',
    scope: SCOPE,
    // Without the tenant, the IdP's form cannot route an invited guest and fails.
    acr_values: `tenant:${IDP_TENANT_ID} idp:invent`,
    stateStore: new WebStorageStateStore({ store: window.sessionStorage })
  });
  return client;
}

export interface IBeginSignIn {
  /** The address to sign in with — what the IdP names as `login_hint` after a redeem. */
  loginHint?: string;
  /** Leave in place of this history entry: a page that only passes the visitor on, which Back
   *  must not return to. */
  replace?: boolean;
}

/**
 * Leaves for the IdP's sign-in. Rejects, with the page still here, when the IdP's discovery
 * document cannot be read.
 */
export async function beginSignIn({ loginHint, replace }: IBeginSignIn = {}): Promise<void> {
  const request = await oidc().createSigninRequest({ login_hint: loginHint });
  if (replace) window.location.replace(request.url);
  else window.location.assign(request.url);
}

export interface ICompletedSignIn {
  /** For survey-api's exchange, once. */
  accessToken: string;
  /** For logout's `id_token_hint`, kept with the session. */
  idToken: string;
  /** The address the IdP signed in, from the id_token: what a "not linked" answer names. */
  email: Nullable<string>;
}

/**
 * Redeems the code on the callback `url` against this tab's stored request. Rejects with
 * `oidc-client-ts`'s `ErrorResponse` for an `error` the IdP sent back, and with a plain `Error`
 * for a URL this tab did not start — no `state`, or one already redeemed — and for a token
 * answer missing either token.
 */
export async function completeSignIn(url: string): Promise<ICompletedSignIn> {
  const response = await oidc().processSigninResponse(url);
  if (!response.access_token || !response.id_token) {
    throw new Error('The IdP answered without an access token and an id_token.');
  }
  const email = response.profile?.email;
  return {
    accessToken: response.access_token,
    idToken: response.id_token,
    email: typeof email === 'string' ? email : null
  };
}

/**
 * The IdP's end session with `idToken` as its hint: opening it signs the person out of Entra too.
 * A portal client has no post-logout redirect, so the person ends on the IdP's own page.
 *
 * Read from the discovery document, or — when that cannot be read — spelled as IdentityServer
 * spells it under the authority: a logout that fell back to `/login` would leave the person
 * signed in at Invent, and the next "Sign in" would open the same identity without asking.
 */
export async function endSessionUrl(idToken: string): Promise<string> {
  try {
    const request = await oidc().createSignoutRequest({ id_token_hint: idToken });
    return request.url;
  } catch (discoveryError) {
    console.warn('[Auth] The IdP’s end session was not read from its discovery:', discoveryError);
    const authority = IDP_AUTHORITY.endsWith('/') ? IDP_AUTHORITY : `${IDP_AUTHORITY}/`;
    const url = new URL('connect/endsession', authority);
    url.searchParams.set('id_token_hint', idToken);
    return url.toString();
  }
}
